import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MODULE, type CertifiedEmailView, type OutboundEmailTestResult } from '@pqrs/contracts';
import { useSession } from '@/app/session';
import { Icono } from '@xiltrion/ui/icono';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { Input } from '@xiltrion/ui/input';
import { ConfirmarDialogo } from '@xiltrion/ui/bloques/confirmar';
import { Spinner } from '@xiltrion/ui/spinner';
import { apiDelete, apiGet, apiPost, apiPut } from '@/api/client';
import { Card, ErrorState, LoadingState, fmtDate } from '@/components/ui';
import { errorMessage } from '@/features/cases/lib';
import { SecretField } from './email-shared';
import { ConfiguredService, Field, notify, notifyError } from './shared';

const KEY = ['config', 'email', 'certified'] as const;
/** Sin el módulo «Correo certificado» no se pregunta: no hay certificado que mostrar (ni columna de rastreo). */
export const useCertifiedEmail = () => useQuery({ queryKey: KEY, queryFn: () => apiGet<CertifiedEmailView>('/communications/certified'), enabled: useSession().hasModule(MODULE.certifiedMail) });

const CLIENT_ID = /^[A-Za-z0-9_-]+$/;
/** La base de la API v1: las rutas firmadas son `/api/v1/correos…`. */
const DEFAULT_ENDPOINT = 'https://clientes.datacreditopostal.com/api';

/** La URL que Datacrédito Postal llama con los estados: la entidad va en la ruta. */
export function certifiedWebhookUrl(slug: string): string {
  const base = (import.meta.env.VITE_API_BASE ?? '/api/v1').replace(/\/$/, '');
  return new URL(`${base}/communications/webhooks/certified/${slug}`, window.location.origin).toString();
}

/** El servicio solo llama a una URL HTTPS pública (sin localhost ni redes privadas). */
function webhookPublica(url: string): boolean {
  const u = new URL(url);
  const h = u.hostname;
  return u.protocol === 'https:' && h !== 'localhost' && !h.endsWith('.localhost') && !h.endsWith('.local')
    && !/^(127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|0\.|\[)/.test(h);
}

interface Form { endpoint: string; clientId: string; secret: string }
function initialForm(v: CertifiedEmailView | undefined): Form {
  return { endpoint: v?.settings?.endpoint ?? DEFAULT_ENDPOINT, clientId: v?.settings?.clientId ?? '', secret: '' };
}

/** Configuración › Correo saliente › Certificado de comunicaciones (Datacrédito Postal). */
export function CertifiedEmailCard() {
  const qc = useQueryClient();
  const q = useCertifiedEmail();
  const v = q.data;
  const [f, setF] = useState<Form>(() => initialForm(undefined));
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  useEffect(() => { if (v) setF(initialForm(v)); }, [v?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const saved = v?.settings ?? null;
  /** Con la suite conectada, el certificado lo configura Datalogic allá: aquí se ve, se prueba y se copia el webhook. */
  const locked = !!v?.managedBySuite;
  const summary = !!saved && !editing;
  const set = (patch: Partial<Form>) => { setF((x) => ({ ...x, ...patch })); test.reset(); };
  const endpoint = f.endpoint.trim();
  const clientId = f.clientId.trim();
  const clientSecret = f.secret.trim();
  /** El secreto guardado sirve si la dirección y el client_id no cambiaron. */
  const storedApplies = !!saved?.hasSecret && saved.endpoint === endpoint && saved.clientId === clientId;
  const missing = !/^https:\/\/[^\s?#]+$/.test(endpoint) ? 'la dirección HTTPS del servicio, sin parámetros'
    : !CLIENT_ID.test(clientId) ? 'el client_id'
    : clientSecret.startsWith(`${clientId}:`) ? 'el client_secret solo, sin el client_id delante'
    : !storedApplies && !clientSecret ? 'el client_secret'
    : null;
  const config = { endpoint, clientId, ...(clientSecret ? { clientSecret } : {}) };

  const onSaved = (nv: CertifiedEmailView, msg: string) => { qc.setQueryData(KEY, nv); notify('success', msg); };
  const save = useMutation({
    mutationFn: () => apiPut<CertifiedEmailView>('/communications/certified', config),
    onSuccess: (nv) => { onSaved(nv, 'Certificado guardado'); setEditing(false); },
    onError: notifyError,
  });
  const remove = useMutation({
    mutationFn: () => apiDelete<CertifiedEmailView>('/communications/certified'),
    onSuccess: (nv) => { onSaved(nv, 'Certificado quitado'); setEditing(false); },
    onError: notifyError,
  });
  /** En el resumen se prueba lo guardado; al editar, lo del formulario. Sin envío: cada envío certificado se cobra. */
  const test = useMutation({
    mutationFn: () => apiPost<OutboundEmailTestResult>('/communications/certified/test', summary ? {} : { config }),
  });

  if (q.isLoading) return <Card title="Certificado de comunicaciones"><LoadingState rows={2} /></Card>;
  if (q.isError || !v) return <Card title="Certificado de comunicaciones"><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card>;
  const url = certifiedWebhookUrl(v.webhookSlug);
  const webhookAviso = !webhookPublica(url) && (
    <Alert variant="warning"><Icono nombre="mdi:alert-outline" /><AlertDescription>Esta dirección no es HTTPS pública: Datacrédito Postal no puede llamarla. Regístrenle la del servidor en producción.</AlertDescription></Alert>
  );
  const cancelEdit = () => { setF(initialForm(saved ? v : undefined)); test.reset(); setEditing(false); };

  const testResult = test.data && (test.data.ok
    ? <Alert variant="success"><Icono nombre="mdi:check-circle" /><AlertTitle>La conexión funciona</AlertTitle><AlertDescription>{test.data.detail}</AlertDescription></Alert>
    : <Alert variant="destructive"><Icono nombre="mdi:alert-circle-outline" /><AlertTitle>No funcionó</AlertTitle><AlertDescription className="break-words">{test.data.error}</AlertDescription></Alert>);
  const removeDialog = (
    <ConfirmarDialogo abierto={removing} onAbiertoChange={setRemoving} destructivo titulo="Quitar el certificado" textoConfirmar="Quitar"
      descripcion="Los envíos que salen con certificado dejarán de poder enviar hasta que se configure de nuevo. Los estados y actas de los envíos ya hechos siguen guardados."
      onConfirmar={() => remove.mutateAsync().then(() => undefined)} />
  );

  const suiteNote = locked && (v.source === 'tenant'
    ? <Alert variant="warning"><Icono nombre="mdi:swap-horizontal" /><AlertDescription>SGDEA ya no envía con este: el certificado sale solo de la suite. Datalogic lo pasa allá con <code>pnpm suite:mail-export</code>.</AlertDescription></Alert>
    : <Alert variant="info"><Icono nombre="mdi:apps" /><AlertDescription>El certificado de la entidad lo configura Datalogic en la suite. La dirección del webhook sí es de SGDEA: regístrenla en la credencial de Datacrédito Postal.</AlertDescription></Alert>);

  if (locked && !saved) {
    return (
      <Card title="Certificado de comunicaciones">
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground m-0">La entidad no tiene certificado en la suite. Para enviar con certificado, Datalogic lo configura allá y elige qué envíos lo usan (ficha de la entidad › Correo › Envíos por app).</p>
          <Field label="Dirección del webhook" htmlFor="ce-webhook-ro" hint="Para registrarla en la credencial cuando la tengan."><Input id="ce-webhook-ro" readOnly className="font-mono text-xs" value={url} onFocus={(e) => e.currentTarget.select()} /></Field>
        </div>
      </Card>
    );
  }

  if (summary && saved) {
    return (
      <Card title="Certificado de comunicaciones">
        <ConfiguredService icon="mdi:email-check-outline" title="Datacrédito Postal configurado: qué se certifica se elige en la suite, envío por envío"
          items={[{ label: 'Servicio', value: saved.endpoint }, { label: 'client_id', value: saved.clientId }, { label: 'Webhook', value: url }]}
          actions={<>
            {!locked && <Button type="button" size="sm" variant="outline" onClick={() => { test.reset(); setEditing(true); }}><Icono nombre="mdi:pencil-outline" /> Editar</Button>}
            <Button type="button" size="sm" variant="outline" disabled={test.isPending} onClick={() => test.mutate()}>{test.isPending ? <Spinner /> : <Icono nombre="mdi:lan-connect" />} Probar conexión</Button>
            {!locked && <Button type="button" size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setRemoving(true)}><Icono nombre="mdi:trash-can-outline" /> Quitar</Button>}
            {v.updatedAt && <span className="text-xs text-muted-foreground ml-auto">Guardado {fmtDate(v.updatedAt)}{v.updatedBy ? ` · ${v.updatedBy}` : ''}</span>}
          </>}>
          {suiteNote}
          {webhookAviso}
          {testResult}
        </ConfiguredService>
        {removeDialog}
      </Card>
    );
  }

  return (
    <Card title="Certificado de comunicaciones">
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (!missing) save.mutate(); }}>
        <p className="text-sm text-muted-foreground m-0">
          La conexión con Datacrédito Postal para enviar al ciudadano y al juzgado con certificado: acuse de entrega, rebote o lectura, con acta. Qué se certifica lo elige la entidad en la suite, envío por envío (ficha de la entidad › Correo › Envíos por app); solo un correo al ciudadano o al juzgado de un expediente sale certificado.
        </p>

        <Alert variant="info"><Icono nombre="mdi:information-outline" /><AlertDescription>La credencial son dos datos que entrega Datacrédito Postal: el <code>client_id</code> y el <code>client_secret</code> (este solo firma las peticiones; nunca se envía). Las IPs públicas de este servidor deben estar registradas en la credencial, y el reloj sincronizado (±5 minutos). Registren la dirección del webhook en la credencial: por ahí llegan los estados y las actas.</AlertDescription></Alert>

        <Field label="Dirección del servicio" htmlFor="ce-endpoint" hint="La base de la API, con https:// y su /api, sin parámetros."><Input id="ce-endpoint" autoComplete="off" placeholder={DEFAULT_ENDPOINT} value={f.endpoint} onChange={(e) => set({ endpoint: e.target.value })} /></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="client_id" htmlFor="ce-client-id"><Input id="ce-client-id" autoComplete="off" className="font-mono" value={f.clientId} onChange={(e) => set({ clientId: e.target.value })} /></Field>
          <SecretField id="ce-secret" label="client_secret" value={f.secret} onChange={(secret) => set({ secret })} stored={storedApplies} />
        </div>
        <Field label="Dirección del webhook" htmlFor="ce-webhook" hint="Se la dan a Datacrédito Postal para que les envíe los estados y las actas.">
          <div className="flex gap-2">
            <Input id="ce-webhook" readOnly className="font-mono text-xs" value={url} onFocus={(e) => e.currentTarget.select()} />
            <Button type="button" size="sm" variant="outline" onClick={() => { void navigator.clipboard.writeText(url).then(() => notify('success', 'Dirección copiada'), () => notify('error', 'No se pudo copiar', 'Selecciónela y cópiela a mano')); }}>
              <Icono nombre="mdi:content-copy" /> Copiar
            </Button>
          </div>
        </Field>
        {webhookAviso}

        {testResult}
        {test.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(test.error)}</AlertDescription></Alert>}
        {missing && <p className="text-xs text-muted-foreground m-0">Falta {missing}.</p>}

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm" disabled={!!missing || save.isPending}>{save.isPending ? <Spinner /> : <Icono nombre="mdi:check" />} Guardar</Button>
          {saved && <Button type="button" size="sm" variant="ghost" onClick={cancelEdit}>Cancelar</Button>}
          <Button type="button" size="sm" variant="outline" disabled={!!missing || test.isPending} onClick={() => test.mutate()}>{test.isPending ? <Spinner /> : <Icono nombre="mdi:lan-connect" />} Probar conexión</Button>
          {saved && <Button type="button" size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setRemoving(true)}><Icono nombre="mdi:trash-can-outline" /> Quitar</Button>}
        </div>
      </form>
      {removeDialog}
    </Card>
  );
}
